import React, { useEffect, useState } from "react";
import { useSocialStore } from "../../../stores/social";
import { Input } from "../../ui/inputs/Input";
import {
  deleteCommentary,
  postCommentary,
  updateCommentary,
  updateEmployee,
} from "../../../api/social";
import { showToastError, showToastSuccess } from "../../../services/toast";
import { getOneCustomerFile } from "../../../api/customerFile";
import { months } from "../../../data/social";
import ListDocs from "../documents/ListDocs";
import { TextArea } from "../../ui/inputs/TextArea";
import { useTranslation } from "react-i18next";
import { ActionButton, DetailCard } from "../../ui/page/PageShell";
import { MicroLabel } from "../../ui/tables/tableUi";

const Informations = () => {
  const { t } = useTranslation();
  const {
    formData,
    setFormData,
    idEmployee,
    idCustomer,
    setCustomer,
    customer,
    setUpdateSocial,
    updateSocial,
  } = useSocialStore();

  const [month, setMonth] = useState<string>("");

  // Récupère les informations du client pour afficher le nom de l'entreprise ainsi que le mois
  useEffect(() => {
    getOneCustomerFile(idCustomer)
      .then((res) => {
        setCustomer(res.data);
        const match = months.find(
          (month: any) => month.value == res?.data?.sc_Month,
        );
        setMonth(match?.label || "");
      })
      .catch(() => {
        showToastError(t("Une erreur est survenue"));
      });
  }, [idEmployee]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUpdateEmployee = () => {
    updateEmployee(Number(idEmployee), formData)
      .then(() => {
        showToastSuccess(t("Salarié modifié avec succès"));
      })
      .catch(() => {
        showToastError(t("Une erreur est survenue"));
      });
  };

  const handleCommentary = () => {
    // Si il y a un id de commentaire, on le modifie
    if (formData?.idCommentary && formData?.commentary) {
      updateCommentary(
        idEmployee,
        { data: formData?.commentary },
        formData?.idCommentary,
      )
        .then(() => {
          showToastSuccess(t("Commentaire modifié avec succès"));
          setUpdateSocial(!updateSocial);
        })
        .catch(() => {
          showToastError(t("Une erreur est survenue"));
        });
    } else if (formData?.commentary && formData?.idCommentary === undefined) {
      // Si il y a pas de commentaire, on l'ajoute
      postCommentary(idEmployee, { data: formData?.commentary })
        .then(() => {
          showToastSuccess(t("Commentaire ajouté avec succès"));
          setUpdateSocial(!updateSocial);
        })
        .catch(() => {
          showToastError(t("Une erreur est survenue"));
        });
    } else if (formData?.commentary === "" && formData?.idCommentary) {
      deleteCommentary(idEmployee, formData?.idCommentary)
        .then(() => {
          showToastSuccess(t("Commentaire supprimé avec succès"));
          setUpdateSocial(!updateSocial);
        })
        .catch(() => {
          showToastError(t("Une erreur est survenue"));
        });
    } else {
      showToastError(t("Une erreur est survenue"));
    }
  };

  const fullName = [formData?.firstName, formData?.lastName]
    .filter(Boolean)
    .join(" ");

  return (
    <aside className="flex h-full w-80 shrink-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white font-Nunito 2xl:w-90">
      {/* Identité du salarié et contexte du dossier */}
      <header className="shrink-0 border-b border-slate-100 px-4 py-3">
        <MicroLabel>{t("Informations")}</MicroLabel>
        <h3
          className="mt-1 truncate text-base font-bold tracking-tight text-slate-900"
          title={fullName}
        >
          {fullName || t("Informations du salarié")}
        </h3>

        <dl className="mt-2.5 space-y-1.5 rounded-xl bg-slate-50 px-3 py-2.5 ring-1 ring-slate-100 ring-inset">
          <div className="flex items-baseline justify-between gap-3">
            <dt>
              <MicroLabel>{t("Entreprise")}</MicroLabel>
            </dt>
            <dd
              className="min-w-0 truncate text-xs font-semibold text-slate-700"
              title={customer?.name}
            >
              {customer?.name || "—"}
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <dt>
              <MicroLabel>{t("Période en cours")}</MicroLabel>
            </dt>
            <dd className="text-xs font-semibold text-indigo-700">
              {month || t("Chargement...")}
            </dd>
          </div>
        </dl>

        {formData?.isFinished === true && (
          <p className="mt-2 flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-100 ring-inset">
            <svg
              aria-hidden="true"
              className="size-3.5 shrink-0"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.4}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
            {t("Fin de saisie effectuée")}
          </p>
        )}
      </header>

      {/* Fiche salarié, commentaire et documents */}
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-slate-50/70 p-3">
        <DetailCard title={t("Informations du salarié")} bodyClassName="p-3">
          <div className="space-y-2.5">
            <Input
              type="text"
              label={t("Matricule")}
              name="matricule"
              value={formData?.matricule || ""}
              required
              onChange={handleChange}
              clasName="w-full"
            />
            <Input
              label={t("Prénom")}
              name="firstName"
              value={formData?.firstName || ""}
              required
              onChange={handleChange}
              clasName="w-full"
            />
            <Input
              label={t("Nom")}
              name="lastName"
              value={formData?.lastName || ""}
              required
              onChange={handleChange}
              clasName="w-full"
            />
          </div>

          <div className="mt-3 flex justify-end">
            <ActionButton
              variant="soft"
              onClick={(e) => {
                e.preventDefault();
                handleUpdateEmployee();
              }}
            >
              {t("Modifier")}
            </ActionButton>
          </div>
        </DetailCard>

        <DetailCard title={t("Commentaire")} bodyClassName="p-3">
          <TextArea
            name="commentary"
            value={formData?.commentary || ""}
            clasName="w-full"
            onChange={(e) => {
              setFormData({ ...formData, commentary: e.target.value });
            }}
          />

          <div className="mt-3 flex justify-end">
            <ActionButton
              variant="soft"
              onClick={(e) => {
                e.preventDefault();
                handleCommentary();
              }}
            >
              {formData?.idCommentary ? t("Modifier") : t("Ajouter")}
            </ActionButton>
          </div>
        </DetailCard>

        <DetailCard
          title={t("Documents du salarié")}
          className="overflow-hidden"
          bodyClassName=""
        >
          <ListDocs scroll={false} />
        </DetailCard>
      </div>
    </aside>
  );
};

export default Informations;
